/**
 * 보낸 전문 슬라이드 본문 — 추출 프롬프트(단계별)의 system/user/tool 원문을 보인다.
 *
 * spec 2026-09-30 화면설계 5. 자기 react-query를 가진다(DoseRuleExtractionInfo는 순수
 * 프리젠테이션 유지, AC-45: 슬라이드가 열려 마운트될 때만 요청). 섹션 3개(기본 접힘)에서
 * 섹션별 sha가 missing이면 "보관본 없음", 아니면 저장 바이트 그대로 <pre>로 표시·복사한다.
 *
 * @spec:2026-09-30-처방검토-룰추출-프롬프트-버전관리-스냅샷
 */
import React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import { ToastManager } from "components/organisms/CommonUI";
import { doseRuleAdminApi } from "services/api/doseRules/doseRuleAdminApi";
import {
  AdminDosePromptTextsData,
  DosePromptRef,
} from "services/api/doseRules/types";
import type { PromptTextsStep } from "./openPromptTextsSlide";

export interface PromptTextsSlideContentProps {
  step: PromptTextsStep;
  ruleId: string;
  promptRef: DosePromptRef;
  isDerived: boolean;
  derivedFrom: string | null;
}

const Wrap = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  margin: 0;
  word-break: break-all;
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 0;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  font-size: 13px;
`;

const Section = styled.details`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 6px;
  padding: 8px 10px;

  summary {
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    color: ${COLOR_SYSTEM.get("Gray")?.[700]};
    list-style: revert;
  }
`;

const SectionBody = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CopyRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Pre = styled.pre`
  margin: 0;
  max-height: 60vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 4px;
  padding: 10px;
`;

const Missing = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

type SectionSpec = { label: string; sha: string };

const copy = async (body: string) => {
  try {
    // clipboard 부재(비보안 컨텍스트 등)도 실패로 본다 — optional chaining으로 undefined를 삼켜
    // "복사했습니다"를 거짓 보고하지 않게 한다(§6.1(i)).
    if (!navigator.clipboard) throw new Error("clipboard unavailable");
    await navigator.clipboard.writeText(body);
    ToastManager.show({ content: "복사했습니다", duration: 2000 });
  } catch {
    ToastManager.show({ content: "복사하지 못했습니다", duration: 2000 });
  }
};

const PromptTextsSlideContent: React.FC<PromptTextsSlideContentProps> = ({
  step,
  ruleId,
  promptRef,
  isDerived,
  derivedFrom,
}) => {
  const sections: SectionSpec[] = [
    { label: "시스템 프롬프트", sha: promptRef.system_sha256 },
    { label: "사용자 메시지", sha: promptRef.user_sha256 },
    { label: "출력 양식", sha: promptRef.tool_sha256 },
  ];
  const shas = sections.map((s) => s.sha);

  // 뒤로가기 후 남은 슬라이드를 오독하지 않게 단계·룰을 제목에 박는다. 전개본의 user 메시지는
  // 대표 품목의 것이라 전개본 변형을 쓴다(대표 룰 #derivedFrom).
  const title =
    isDerived && derivedFrom
      ? `보낸 전문 — ${step} · 전개본 (대표 룰 #${derivedFrom} 추출 전문)`
      : `보낸 전문 — ${step} · 룰 #${ruleId}`;

  const query = useQuery<AdminDosePromptTextsData, Error>({
    queryKey: ["dosePromptTexts", shas.join(",")],
    queryFn: async () => {
      const res = await doseRuleAdminApi.getPromptTexts(shas);
      if (res.error !== 0 || !res.data) {
        throw new Error(res.message || "보낸 전문을 불러오지 못했습니다");
      }
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false, // 자동 재시도 없이 [다시 시도] 버튼으로만(AC-47b)
  });

  return (
    <Wrap data-testid="prompt-texts-slide">
      <Title>{title}</Title>

      {query.isLoading ? (
        <Center>
          <Spinner size="Medium" />
        </Center>
      ) : query.isError || !query.data ? (
        <Center>
          <div>보낸 전문을 불러오지 못했습니다</div>
          <Button
            size="Small"
            type="Text"
            variant="Outlined"
            color="Primary"
            onClick={() => query.refetch()}
          >
            다시 시도
          </Button>
        </Center>
      ) : (
        (() => {
          const byShaBody = new Map(query.data.texts.map((t) => [t.sha256, t.body]));
          const missing = new Set(query.data.missing);
          return sections.map((section) => {
            const body = byShaBody.get(section.sha);
            const isMissing = missing.has(section.sha) || body === undefined;
            return (
              <Section key={section.label}>
                <summary>{section.label}</summary>
                <SectionBody>
                  {isMissing ? (
                    <Missing>보관본 없음({section.sha.slice(0, 12)})</Missing>
                  ) : (
                    <>
                      <CopyRow>
                        <Button
                          size="XSmall"
                          type="Text"
                          variant="Tinted"
                          color="Primary"
                          onClick={() => copy(body as string)}
                        >
                          복사
                        </Button>
                      </CopyRow>
                      {/* 저장된 바이트 그대로 표시한다(들여쓰기 금지 — 복사본이 보낸 바이트와 같아야 함).
                          줄바꿈은 CSS pre-wrap으로만 하고 body 문자열은 그대로 복사한다. */}
                      <Pre>{body}</Pre>
                    </>
                  )}
                </SectionBody>
              </Section>
            );
          });
        })()
      )}
    </Wrap>
  );
};

export default PromptTextsSlideContent;
